import { useState } from "react";
import { ConnectionFormDataController } from "../formController";
import { Version } from "../../types/version";
import { Person } from "../../types/person";
import {
  ContributorConnection,
  SystemContributorTypes,
} from "../../types/contributor";
import useContributorQuery from "../../hooks/query/connections/useContributorQuery";
import { CreateEdgeData } from "../../hooks/query/connections/connectionQueryClient";
import useContributorLogic from "../../hooks/logic/connections/contributorLogic";
import { extractReferenceKey } from "../../components/forms/fields/basic/EntitySelectionField";

interface ContributorController {
  from?: Version | null;
  to?: Person | null;
  contributorType: SystemContributorTypes;
  systemLanguageType: ContributorConnection["system_language_type"];
}

const useContributorConnectionController = ({
  from = null,
  to = null,
  contributorType,
  systemLanguageType,
}: ContributorController): ConnectionFormDataController<
  ContributorConnection,
  Version,
  Person
> => {
  if ((from === null && to === null) || (from !== null && to !== null)) {
    throw new Error("Exactly one of 'from' or 'to' must be specified");
  }

  const [id, setId] = useState<string | null>(null);
  const direction: "inbound" | "outbound" =
    from === null ? "inbound" : "outbound";
  const anchorEntity: Version | Person = (from || to)!;
  const { useFetchById, useCreate, useUpdate } = useContributorQuery(
    contributorType,
    systemLanguageType
  );
  const { metadataFieldNames, getDisplayName, defaultContributor, userEnums } =
    useContributorLogic(from, to, contributorType, systemLanguageType);
  const fetchById = useFetchById(id || "");
  const update = useUpdate();
  const create = useCreate();
  const [isDialogShown, setShowDialog] = useState(false);

  const openCreateDialog = () => {
    setId(null);
    setShowDialog(true);
  };

  const openEditDialog = (id: string) => {
    setId(id);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setId(null);
  };

  const fetchData = async (): Promise<ContributorConnection> => {
    if (id === null) {
      return defaultContributor;
    }
    const res = await fetchById.refetch();
    const connectionData = res.data;
    if (!connectionData || res.isError) {
      throw new Error("Contributor connection not found");
    }
    return connectionData;
  };

  const onSubmit = async (
    data: Partial<ContributorConnection>
  ): Promise<ContributorConnection> => {
    const metadata: Partial<Omit<ContributorConnection, "_from" | "_to">> = {
      status: data.status,
      role: data.role,
      place_identification_status: data.place_identification_status,
      place_id: extractReferenceKey(data.place),
      name_internal_evidence: data.name_internal_evidence,
      name_external_evidence: data.name_external_evidence,
      entity_status_of_proofreading: data.entity_status_of_proofreading,
      notes: data.notes,
      internal_notes: data.internal_notes,
      attributes_metadata: {
        ...defaultContributor.attributes_metadata,
        ...data.attributes_metadata,
      },
    };

    if (isCreateMode) {
      metadata.system_contribution_type = contributorType;
      metadata.system_language_type = systemLanguageType;
    }

    const updateData: CreateEdgeData<ContributorConnection> = {
      _from: data.fromDoc?._id || from?._id || "",
      _to: data.toDoc?._id || to?._id || "",
      metadata,
    };

    let result;
    if (id) {
      result = await update.mutateAsync({
        id,
        data: { _rev: data._rev, ...metadata },
      });
      if (!result || update.isError) {
        throw new Error("Failed to update Contributor Relation");
      }
    } else {
      result = await create.mutateAsync(updateData);
      if (!result || create.isError) {
        throw new Error("Failed to create Contributor Relation");
      }
    }

    setId(null);
    return result;
  };

  const isCreateMode = id === null;

  const title = (data: ContributorConnection) =>
    `${isCreateMode ? "Edit" : "Add"} ${getDisplayName(data)}`;

  return {
    defaultValues: defaultContributor,
    metadataFieldNames,
    isCreateMode,
    userEnums,
    title,
    fetchData,
    onSubmit,
    formCallback: closeDialog,
    openEditDialog,
    openCreateDialog,
    closeDialog,
    isDialogShown,
    direction,
    anchorEntity,
    from: from || null,
    to: to || null,
  };
};

export default useContributorConnectionController;
